import { useState, useEffect, useCallback, useRef } from 'react';
import {
  User as FirebaseUser,
  signInWithPopup,
  signOut as firebaseSignOut,
  onAuthStateChanged,
} from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db, googleProvider, callGetUserDetails, Collections } from '@/lib/firebase';
import {
  clearStoredEmailForSignIn,
  isDashboardEmailSignInLink,
  readStoredEmailForSignIn,
  replaceUrlAfterEmailLinkSignIn,
  sendDashboardEmailSignInLink,
  signInWithEmailLinkOnce,
} from '@/lib/emailLinkAuth';
import type { UserProfile as SharedUserProfile } from '@molish-gym/shared';
import { i18n } from '@/i18n/config';
import { DASHBOARD_LOCALES, type DashboardLocale } from '@/lib/localeStorage';

function currentDashboardLocale(): DashboardLocale | undefined {
  const raw = (i18n.language ?? '').split('-')[0]?.toLowerCase();
  if (raw && (DASHBOARD_LOCALES as readonly string[]).includes(raw)) {
    return raw as DashboardLocale;
  }
  return undefined;
}

type UserDocOwnerFields = {
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
};

/** Top-level `firstName` / `lastName` / `phoneNumber` on the user document. */
function parseOwnerFieldsFromDoc(data: Record<string, unknown>): Partial<UserDocOwnerFields> {
  const out: Partial<UserDocOwnerFields> = {};
  if (typeof data.firstName === 'string') out.firstName = data.firstName;
  if (typeof data.lastName === 'string') out.lastName = data.lastName;
  if (typeof data.phoneNumber === 'string') out.phoneNumber = data.phoneNumber;
  return out;
}

/** Merged view: shared user profile fields from Firestore + Auth SDK identity. */
export interface UserProfile extends SharedUserProfile {
  email: string;
  displayName: string;
  photoUrl: string | null;
}

export type TryFinishEmailLinkResult =
  | { status: 'not_link' }
  | { status: 'needs_email' }
  | { status: 'completed' };

interface UseAuthResult {
  user: FirebaseUser | null;
  profile: UserProfile | null;
  loading: boolean;
  error: Error | null;
  signInWithGoogle: () => Promise<void>;
  sendEmailSignInLink: (email: string) => Promise<void>;
  tryFinishEmailLinkSignIn: () => Promise<TryFinishEmailLinkResult>;
  finishEmailLinkSignInWithEmail: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

export function useAuth(): UseAuthResult {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  
  // Keep track of current Firebase user for Firestore subscription
  const currentUserRef = useRef<FirebaseUser | null>(null);

  // Subscribe to user profile from Firestore, merging with Firebase Auth data
  const subscribeToProfile = useCallback((firebaseUser: FirebaseUser): (() => void) => {
    currentUserRef.current = firebaseUser;
    const userRef = doc(db, Collections.USERS, firebaseUser.uid);
    
    return onSnapshot(
      userRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data() as Record<string, unknown>;
          const ownerFields = parseOwnerFieldsFromDoc(data);
          const userProfile: UserProfile = {
            userId: firebaseUser.uid,
            email: firebaseUser.email || '',
            displayName: firebaseUser.displayName || 'User',
            photoUrl: firebaseUser.photoURL,
            settings: (data.settings as UserProfile['settings']) || { theme: 'system' },
            notifications: {
              enabled: ((data.notifications as { registeredDeviceCount?: number } | undefined)
                ?.registeredDeviceCount || 0) > 0,
              tokenCount:
                (data.notifications as { registeredDeviceCount?: number } | undefined)
                  ?.registeredDeviceCount || 0,
            },
            ...ownerFields,
            lastLoginAt: typeof data.lastLoginAt === 'string' ? data.lastLoginAt : '',
          };
          setProfile(userProfile);
        } else {
          setProfile(null);
        }
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching profile:', err);
        setError(err);
        setLoading(false);
      }
    );
  }, []);

  /**
   * Fire-and-forget call that ensures the `/users/{uid}` document exists, refreshes
   * `lastLoginAt`, persists the active locale, and triggers the first-login welcome email.
   * Runs in the background — the UI is gated on the Firestore snapshot below, not on this
   * callable, so a cold start of `getUserDetails` does not block first paint and runs in
   * parallel with `getMyStores`.
   */
  const ensureProfileExists = useCallback((): void => {
    const locale = currentDashboardLocale();
    void callGetUserDetails(locale ? { locale } : {}).catch((err) => {
      console.error('Failed to ensure profile exists:', err);
      setError(err as Error);
    });
  }, []);

  // Listen to auth state changes and subscribe to profile
  useEffect(() => {
    let unsubscribeProfile: (() => void) | null = null;

    const unsubscribeAuth = onAuthStateChanged(
      auth,
      (firebaseUser) => {
        setUser(firebaseUser);

        // Cleanup previous profile subscription
        if (unsubscribeProfile) {
          unsubscribeProfile();
          unsubscribeProfile = null;
        }

        if (firebaseUser) {
          // Attach the snapshot listener first so existing-user data renders immediately
          // (no callable cold start on the critical path). Kick off the ensure-call in
          // parallel — it creates the doc on first login and updates lastLoginAt, and the
          // snapshot picks up the resulting writes when they land.
          unsubscribeProfile = subscribeToProfile(firebaseUser);
          ensureProfileExists();
        } else {
          setProfile(null);
          setLoading(false);
        }
      },
      (err) => {
        console.error('Auth state change error:', err);
        setError(err);
        setLoading(false);
      }
    );

    return () => {
      unsubscribeAuth();
      if (unsubscribeProfile) {
        unsubscribeProfile();
      }
    };
  }, [subscribeToProfile, ensureProfileExists]);

  const signInWithGoogle = async () => {
    try {
      setError(null);
      setLoading(true);
      
      // Sign in with Google - profile subscription handled by auth state listener
      await signInWithPopup(auth, googleProvider);
    } catch (err) {
      setError(err as Error);
      setLoading(false);
      throw err;
    }
  };

  const sendEmailSignInLink = async (email: string) => {
    setError(null);
    try {
      await sendDashboardEmailSignInLink(auth, email);
    } catch (err) {
      clearStoredEmailForSignIn();
      setError(err as Error);
      throw err;
    }
  };

  const tryFinishEmailLinkSignIn = useCallback(async (): Promise<TryFinishEmailLinkResult> => {
    if (typeof window === 'undefined') {
      return { status: 'not_link' };
    }
    const href = window.location.href;
    if (!isDashboardEmailSignInLink(auth, href)) {
      return { status: 'not_link' };
    }

    const stored = readStoredEmailForSignIn();
    if (!stored) {
      return { status: 'needs_email' };
    }

    setError(null);
    setLoading(true);
    try {
      await signInWithEmailLinkOnce(auth, stored, href);
      clearStoredEmailForSignIn();
      replaceUrlAfterEmailLinkSignIn();
      return { status: 'completed' };
    } catch (err) {
      if (auth.currentUser) {
        clearStoredEmailForSignIn();
        replaceUrlAfterEmailLinkSignIn();
        return { status: 'completed' };
      }
      clearStoredEmailForSignIn();
      setError(err as Error);
      setLoading(false);
      throw err;
    }
  }, []);

  const finishEmailLinkSignInWithEmail = useCallback(async (email: string) => {
    if (typeof window === 'undefined') {
      return;
    }
    const href = window.location.href;
    if (!isDashboardEmailSignInLink(auth, href)) {
      throw new Error('Invalid sign-in link');
    }
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) {
      throw new Error('Email required');
    }

    setError(null);
    setLoading(true);
    try {
      await signInWithEmailLinkOnce(auth, trimmed, href);
      clearStoredEmailForSignIn();
      replaceUrlAfterEmailLinkSignIn();
    } catch (err) {
      if (auth.currentUser) {
        clearStoredEmailForSignIn();
        replaceUrlAfterEmailLinkSignIn();
        return;
      }
      clearStoredEmailForSignIn();
      setError(err as Error);
      setLoading(false);
      throw err;
    }
  }, []);

  const signOut = async () => {
    try {
      setError(null);
      await firebaseSignOut(auth);
      setProfile(null);
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  };

  const refreshProfile = async () => {
    // Profile updates automatically via Firestore subscription
    // This function is kept for API compatibility but is now a no-op
  };

  return {
    user,
    profile,
    loading,
    error,
    signInWithGoogle,
    sendEmailSignInLink,
    tryFinishEmailLinkSignIn,
    finishEmailLinkSignInWithEmail,
    signOut,
    refreshProfile,
  };
}
